import React, { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { PageTitle } from '../../../../../../../_metronic/layout/core'
import { TablePagination } from '../../../../../../../components/Table/TablePagination'
import { FilterManager } from '../../../../../../common/filter/FilterManager'
import { IExternalTransaction } from '../../core/response.types'
import { ExternalTransactionListQuery } from '../../core/query.types'
import { DEFAULT_EXTERNAL_TRANSACTION_QUERY, DEFAULT_EXTERNAL_TRANSACTIONS_FILTERS } from '../../core/query.defaults'
import { EXTERNAL_TRANSACTIONS_BREADCRUMBS, SEARCH_FIELD_OPTIONS } from '../../core/consts'
import { getExternalTransactions } from '../../core/api'
import { IExternalTransactionsFilters } from '../../core/types'
import { useAuth } from '../../../../../../modules/auth'
import { ExternalTransactionsTable } from './ExternalTransactionsTable'
import {
  ADAPTER_FILTER_LABEL,
  ADAPTER_ID_SEARCH_ERROR,
  adapterIdPayload,
  isAdapterScopeRequired,
  isAdapterSearchBlocked,
  usePaymentAdapterOptions,
} from '../../../../../../common/filter/adapterIdSearch'

const AppSwal = withReactContent(Swal)

export const ExternalTransactionList: React.FC = () => {
  const [items, setItems] = useState<IExternalTransaction[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [activeFilters, setActiveFilters] = useState<IExternalTransactionsFilters>(DEFAULT_EXTERNAL_TRANSACTIONS_FILTERS)
  const { currentUser } = useAuth()
  const adapterRequired = isAdapterScopeRequired(currentUser)
  const [searchError, setSearchError] = useState('')
  const adapterOptions = usePaymentAdapterOptions(adapterRequired)

  const pageCount = Math.ceil(totalRows / DEFAULT_EXTERNAL_TRANSACTION_QUERY.perPage)

  const buildQuery = (page: number, filters: IExternalTransactionsFilters): ExternalTransactionListQuery => {
    const search = filters.searchAdvanced
    const searchValue = search?.query?.trim() || undefined

    return {
      ...DEFAULT_EXTERNAL_TRANSACTION_QUERY,
      page: page + 1,
      provider: filters.provider || undefined,
      ticketToken: search?.field === 'ticketToken' ? searchValue : undefined,
      merchantTransactionId: search?.field === 'merchantTransactionId' ? searchValue : undefined,
      adapterTransactionId: search?.field === 'adapterTransactionId' ? searchValue : undefined,
      ...adapterIdPayload(adapterRequired, search, 'adapterTransactionId', filters.paymentAdapterId),
    }
  }

  const loadData = useCallback(async (page: number, filters: IExternalTransactionsFilters) => {
    setIsLoading(true)
    try {
      const { data } = await getExternalTransactions(buildQuery(page, filters))
      if (data.success) {
        setItems(data.data ?? [])
        setTotalRows(data.totalRows ?? 0)
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error)
      AppSwal.fire({ icon: 'error', title: 'Ошибка', text: 'Не удалось загрузить данные' })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData(0, DEFAULT_EXTERNAL_TRANSACTIONS_FILTERS)
  }, [])

  const handleApplyFilters = (filters: IExternalTransactionsFilters) => {
    if (isAdapterSearchBlocked(adapterRequired, filters.searchAdvanced, 'adapterTransactionId', filters.paymentAdapterId)) {
      setSearchError(ADAPTER_ID_SEARCH_ERROR)
      return
    }
    setSearchError('')
    setActiveFilters(filters)
    setCurrentPage(0)
    loadData(0, filters)
  }

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected)
    loadData(selected, activeFilters)
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={EXTERNAL_TRANSACTIONS_BREADCRUMBS} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Внешние транзакции</span>
          </h3>
        </div>
      </div>

      <FilterManager<IExternalTransactionsFilters>
        activeFilters={[
          'searchAdvanced',
          { filter: 'select', fieldName: 'paymentAdapterId', visibility: adapterRequired },
          // { filter: 'select', fieldName: 'provider' },
        ]}
        defaultValues={DEFAULT_EXTERNAL_TRANSACTIONS_FILTERS}
        onApplyFilters={handleApplyFilters}
        filterProps={{
          searchAdvanced: {
            options: SEARCH_FIELD_OPTIONS,
            placeholder: 'Введите значение...',
            error: searchError,
          },
          paymentAdapterId: {
            label: ADAPTER_FILTER_LABEL,
            options: adapterOptions,
            placeholder: 'Не выбран',
          },
          // provider: {
          //   label: 'Провайдер',
          //   options: [],
          //   placeholder: 'Все провайдеры',
          // },
        }}
      />

      <div className='row g-5 g-xl-8 mb-7'>
        <div className='col'>
          <div className='card'>
            <div className='card-body'>
              <div className='card-rounded w-100'>
                <div className='d-flex flex-column'>
                  <ExternalTransactionsTable items={items} isLoading={isLoading} />
                  <TablePagination
                    currentPage={currentPage}
                    pageCount={pageCount}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

